import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { 
  Search, 
  ChevronUp, 
  ChevronDown, 
  Save, 
  RefreshCw,
  Filter,
  Download,
  Upload,
  FileDown,
  Eye,
  EyeOff,
  Trash2,
  AlertTriangle,
  Key,
  Copy,
  RotateCw
} from 'lucide-react';
import HelpButton from './HelpButton';

const DatabaseManagement = () => {
  const [activeTab, setActiveTab] = useState('members'); // 'members' ou 'cotisations'
  const [members, setMembers] = useState([]);
  const [cotisations, setCotisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnWidths, setColumnWidths] = useState({});
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [saveIndicator, setSaveIndicator] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [hoveredPassword, setHoveredPassword] = useState(null);

  // Définir les colonnes éditables
  const columns = [
    { key: 'first_name', label: 'Prénom', editable: true, type: 'text' },
    { key: 'last_name', label: 'Nom', editable: true, type: 'text' },
    { key: 'email', label: 'Email', editable: true, type: 'email' },
    { key: 'phone', label: 'Téléphone', editable: true, type: 'tel' },
    { key: 'gsm', label: 'GSM', editable: true, type: 'tel' },
    { key: 'role', label: 'Rôle', editable: true, type: 'select', options: ['admin', 'membre_vie', 'membre_annuel', 'membre_exterieur', 'prospect', 'non_actif', 'decede'] },
    { key: 'civilite', label: 'Civilité', editable: true, type: 'text' },
    { key: 'language', label: 'Langue', editable: true, type: 'select', options: ['FR', 'NL', 'EN'] },
    { key: 'date_naissance', label: 'Date naissance', editable: true, type: 'date' },
    { key: 'address', label: 'Adresse', editable: true, type: 'text' },
    { key: 'city', label: 'Ville', editable: true, type: 'text' },
    { key: 'postal_code', label: 'Code postal', editable: true, type: 'text' },
    { key: 'country', label: 'Pays', editable: true, type: 'text' },
    { key: 'lignage', label: 'Lignage', editable: true, type: 'text' },
    { key: 'admin_notes', label: 'Notes admin', editable: true, type: 'textarea' },
    { key: 'membership_date', label: 'Date adhésion', editable: true, type: 'date' },
    { key: 'profile_picture', label: 'Photo profil', editable: true, type: 'url' },
    { key: 'blason_familial', label: 'Blason familial', editable: true, type: 'url' },
    { key: 'rgpd_consent', label: 'RGPD', editable: true, type: 'boolean' },
    { key: 'is_active', label: 'Actif', editable: true, type: 'boolean' },
    { key: 'created_at', label: 'Créé le', editable: false, type: 'datetime' },
    { key: 'updated_at', label: 'Modifié le', editable: false, type: 'datetime' }
  ];

  useEffect(() => {
    fetchMembers();
    fetchCotisations();
    fetchMultiCotisations();
    fetchDonations();
  }, []);

  const [multiCotisations, setMultiCotisations] = useState([]);
  const [donations, setDonations] = useState([]);

  const fetchMultiCotisations = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/multi-cotisations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMultiCotisations(data.multi_cotisations || []);
      }
    } catch (error) {
      console.error('Erreur chargement paiements groupés:', error);
    }
  };

  const fetchDonations = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/donations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDonations(data.donations || []);
      }
    } catch (error) {
      console.error('Erreur chargement donations:', error);
    }
  };

  const markDonationPaid = async (donationId, userName) => {
    if (!window.confirm(`Marquer la donation de ${userName} comme payée ?`)) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/donations/${donationId}/mark-paid`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Donation marquée comme payée !');
        fetchDonations();
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur marquage donation:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const markDonationPending = async (donationId, userName) => {
    if (!window.confirm(`Remettre la donation de ${userName} en attente ?`)) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/donations/${donationId}/mark-pending`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Donation remise en attente');
        fetchDonations();
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur remise en attente donation:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const deleteDonation = async (donationId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la donation de ${userName} ?`)) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/donations/${donationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDonations(prev => prev.filter(d => d.id !== donationId));
        alert('Donation supprimée avec succès');
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur suppression donation:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const markMultiCotisationPaid = async (multiCotisationId, payerName) => {
    if (!window.confirm(`Marquer le paiement groupé de ${payerName} comme payé ? Cela mettra aussi à jour toutes les cotisations associées.`)) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/multi-cotisations/${multiCotisationId}/mark-paid`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Paiement groupé marqué comme payé !');
        fetchMultiCotisations();
        fetchCotisations(); // Rafraîchir aussi les cotisations individuelles
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur marquage paiement groupé:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const deleteMultiCotisation = async (multiCotisationId, payerName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le paiement groupé de ${payerName} ? Les cotisations individuelles ne seront pas supprimées.`)) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/multi-cotisations/${multiCotisationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMultiCotisations(prev => prev.filter(m => m.id !== multiCotisationId));
        alert('Paiement groupé supprimé avec succès');
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur suppression paiement groupé:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const markMultiCotisationPending = async (multiCotisationId, payerName) => {
    if (!window.confirm(`Remettre le paiement groupé de ${payerName} en attente ? Les cotisations individuelles seront aussi remises en attente.`)) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/multi-cotisations/${multiCotisationId}/mark-pending`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Paiement groupé remis en attente');
        fetchMultiCotisations();
        fetchCotisations();
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur remise en attente paiement groupé:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const fetchCotisations = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCotisations(data.cotisations || []);
      }
    } catch (error) {
      console.error('Erreur chargement cotisations:', error);
    }
  };

  const deleteCotisation = async (cotisationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette cotisation ?')) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/${cotisationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCotisations(prev => prev.filter(c => c.id !== cotisationId));
        alert('Cotisation supprimée avec succès');
      } else {
        const data = await response.json();
        alert(`Erreur: ${data.detail}`);
      }
    } catch (error) {
      console.error('Erreur suppression cotisation:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        console.error('Erreur lors du chargement des membres');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCellEdit = async (memberId, field, newValue) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      // Optimistic update
      const updatedMembers = members.map(member => 
        member.id === memberId ? { ...member, [field]: newValue } : member
      );
      setMembers(updatedMembers);

      // Afficher l'indicateur de sauvegarde
      setSaveIndicator({ ...saveIndicator, [`${memberId}-${field}`]: 'saving' });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: newValue })
      });
      
      if (response.ok) {
        setSaveIndicator({ ...saveIndicator, [`${memberId}-${field}`]: 'saved' });
        setTimeout(() => {
          setSaveIndicator(prev => {
            const updated = { ...prev };
            delete updated[`${memberId}-${field}`];
            return updated;
          });
        }, 2000);
      } else {
        // Rollback en cas d'erreur
        fetchMembers();
        setSaveIndicator({ ...saveIndicator, [`${memberId}-${field}`]: 'error' });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      fetchMembers();
      setSaveIndicator({ ...saveIndicator, [`${memberId}-${field}`]: 'error' });
    }
  };

  const handleDeleteMember = async (member) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      setDeleting(member.id);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${member.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Supprimer le membre de la liste locale
        setMembers(prev => prev.filter(m => m.id !== member.id));
        setDeleteConfirm(null);
      } else {
        console.error('Erreur lors de la suppression');
        alert('Erreur lors de la suppression du membre');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du membre');
    } finally {
      setDeleting(null);
    }
  };

  const confirmDelete = (member) => {
    // Ne pas permettre la suppression des admins
    if (member.role === 'admin') {
      alert('Impossible de supprimer un administrateur');
      return;
    }
    setDeleteConfirm(member);
  };

  const handleExportJSON = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      if (!token) {
        alert('Erreur: Vous devez être connecté pour exporter');
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-json`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `database_complete_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        alert('📦 Export JSON complet terminé avec succès !');
      } else {
        const errorText = await response.text();
        alert(`Erreur d'export JSON: ${response.status} - ${errorText.substring(0, 100)}`);
      }
    } catch (error) {
      console.error('Erreur export JSON:', error);
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleImportJSON = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      alert('Veuillez sélectionner un fichier JSON valide');
      return;
    }
    
    try {
      setImporting(true);
      setImportProgress('Lecture du fichier JSON...');
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      if (!token) {
        alert('Erreur: Vous devez être connecté pour importer');
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      setImportProgress('Upload et traitement...');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-json`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setImportProgress('Terminé !');
        
        const message = `✅ Import JSON terminé avec succès !\n\n` +
          `📊 Résultats :\n` +
          `• Données importées : ${result.imported_count || 0}\n` +
          `• Données mises à jour : ${result.updated_count || 0}\n` +
          `• Total traité : ${result.total_processed || 0}\n` +
          (result.errors && result.errors.length > 0 ? `\n⚠️ Erreurs :\n${result.errors.join('\n')}` : '');
        
        alert(message);
        
        // Recharger les données
        await fetchMembers();
      } else {
        const errorData = await response.json();
        alert(`Erreur lors de l'import JSON:\n${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur import JSON:', error);
      alert(`Erreur lors de l'import JSON: ${error.message}`);
    } finally {
      setImporting(false);
      setImportProgress('');
      // Reset the input
      e.target.value = '';
    }
  };

  // Fonction pour générer un mot de passe sécurisé
  const generateSecurePassword = (length = 12) => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%&*+=-?';
    
    const allChars = lowercase + uppercase + numbers + symbols;
    let password = '';
    
    // Assurer au moins un caractère de chaque type
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += symbols.charAt(Math.floor(Math.random() * symbols.length));
    
    // Remplir le reste
    for (let i = 4; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Mélanger le mot de passe
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  };

  // Fonction pour générer et assigner un mot de passe à un membre
  const generatePasswordForMember = async (memberId) => {
    const newPassword = generateSecurePassword();
    await handleCellEdit(memberId, 'password', newPassword);
    
    // Copier dans le presse-papiers
    try {
      await navigator.clipboard.writeText(newPassword);
      alert(`Nouveau mot de passe généré et copié dans le presse-papiers !\n\nMot de passe: ${newPassword}`);
    } catch (error) {
      alert(`Nouveau mot de passe généré: ${newPassword}\n\n(Copiez-le manuellement)`);
    }
  };

  // Fonction supprimée - les mots de passe sont générés automatiquement côté serveur

  // Fonction pour calculer la largeur optimale du contenu
  const measureTextWidth = (text, font = '14px sans-serif') => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    return Math.ceil(context.measureText(text || '').width);
  };

  // Auto-sizing des colonnes basé sur le contenu
  const autoSizeColumns = () => {
    try {
      const newColumnWidths = {};
      
      columns.filter(col => !hiddenColumns.has(col.key)).forEach(column => {
        let maxWidth = 80; // Largeur minimale
        
        // Mesurer le header
        const headerWidth = measureTextWidth(column.label, 'bold 14px sans-serif') + 60; // +60 pour padding et icônes de tri
        maxWidth = Math.max(maxWidth, headerWidth);
        
        // Mesurer le contenu de chaque cellule visible
        paginatedMembers.forEach(member => {
          let cellContent = '';
          const value = member[column.key];
          
          // Gérer les valeurs vides avec "NC"
          if (!value || value === '' || value === null || value === undefined) {
            cellContent = 'NC';
          } else if (column.type === 'boolean') {
            cellContent = value ? '✓' : '✗';
          } else if (column.type === 'datetime') {
            cellContent = new Date(value).toLocaleString();
          } else if (column.type === 'date') {
            cellContent = new Date(value).toLocaleDateString();
          } else {
            cellContent = String(value);
          }
          
          const cellWidth = measureTextWidth(cellContent) + 24; // +24 pour padding
          maxWidth = Math.max(maxWidth, cellWidth);
        });
        
        // Limiter la largeur maximale pour éviter des colonnes trop larges
        maxWidth = Math.min(maxWidth, 400);
        
        newColumnWidths[column.key] = `${maxWidth}px`;
      });
      
      setColumnWidths(newColumnWidths);
      console.log('Auto-sizing appliqué:', newColumnWidths);
      
    } catch (error) {
      console.error('Erreur lors de l\'auto-sizing:', error);
    }
  };

  // Fonctions de redimensionnement des colonnes
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setResizing(columnKey);
    setStartX(e.clientX);
    const currentWidth = columnWidths[columnKey] || 150; // largeur par défaut
    setStartWidth(parseInt(currentWidth));
  };

  const handleMouseMove = (e) => {
    if (!resizing) return;
    
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(80, startWidth + deltaX); // largeur minimum de 80px
    
    setColumnWidths(prev => ({
      ...prev,
      [resizing]: `${newWidth}px`
    }));
  };

  const handleMouseUp = () => {
    setResizing(null);
    setStartX(0);
    setStartWidth(0);
  };

  // Ajouter les event listeners pour le redimensionnement
  useEffect(() => {
    const handleMouseMoveWrapper = (e) => handleMouseMove(e);
    const handleMouseUpWrapper = () => handleMouseUp();
    
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMoveWrapper);
      document.addEventListener('mouseup', handleMouseUpWrapper);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMoveWrapper);
        document.removeEventListener('mouseup', handleMouseUpWrapper);
      };
    }
  }, [resizing, startX, startWidth]);
  
  // Empêcher la sélection de texte pendant le redimensionnement
  useEffect(() => {
    if (resizing) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [resizing]);

  // Filtrage et tri des données
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = members.filter(member =>
      Object.values(member).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [members, searchTerm, sortConfig]);

  // Pagination
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedMembers.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedMembers, currentPage, pageSize]);

  // Auto-sizing automatique quand le contenu change (optionnel)
  useEffect(() => {
    // Auto-ajuster les colonnes après un délai pour éviter les calculs trop fréquents
    const timeoutId = setTimeout(() => {
      if (paginatedMembers.length > 0 && Object.keys(columnWidths).length === 0) {
        autoSizeColumns();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [paginatedMembers]);

  const totalPages = Math.ceil(filteredAndSortedMembers.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleColumnVisibility = (columnKey) => {
    const newHidden = new Set(hiddenColumns);
    if (newHidden.has(columnKey)) {
      newHidden.delete(columnKey);
    } else {
      newHidden.add(columnKey);
    }
    setHiddenColumns(newHidden);
  };

  const renderCell = (member, column) => {
    const cellKey = `${member.id}-${column.key}`;
    const isEditing = editingCell === cellKey;
    const rawValue = member[column.key];
    const value = rawValue || '';
    const saveStatus = saveIndicator[cellKey];

    // Fonction pour formater la valeur d'affichage
    const getDisplayValue = () => {
      if (!rawValue || rawValue === '' || rawValue === null || rawValue === undefined) {
        return 'NC';
      }
      
      if (column.type === 'datetime') {
        return new Date(rawValue).toLocaleString();
      }
      if (column.type === 'date') {
        return new Date(rawValue).toLocaleDateString();
      }
      if (column.type === 'boolean') {
        return rawValue ? '✓' : '✗';
      }
      
      return rawValue.toString();
    };

    if (!column.editable) {
      const displayValue = getDisplayValue();
      return (
        <span className={displayValue === 'NC' ? 'text-gray-400 italic' : ''}>
          {displayValue}
        </span>
      );
    }

    if (isEditing) {
      if (column.type === 'select') {
        const editValue = rawValue || '';
        return (
          <select
            defaultValue={editValue}
            onBlur={(e) => {
              handleCellEdit(member.id, column.key, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCellEdit(member.id, column.key, e.target.value);
                setEditingCell(null);
              }
              if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            className="w-full px-2 py-1 border rounded"
            autoFocus
          >
            {column.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      } else if (column.type === 'boolean') {
        return (
          <input
            type="checkbox"
            defaultChecked={!!rawValue}
            onChange={(e) => {
              handleCellEdit(member.id, column.key, e.target.checked);
              setEditingCell(null);
            }}
            className="mx-auto"
            autoFocus
          />
        );
      } else if (column.type === 'textarea') {
        const editValue = rawValue || '';
        return (
          <textarea
            defaultValue={editValue}
            onBlur={(e) => {
              handleCellEdit(member.id, column.key, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleCellEdit(member.id, column.key, e.target.value);
                setEditingCell(null);
              }
              if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            className="w-full px-2 py-1 border rounded resize-none"
            rows={2}
            autoFocus
          />
        );
      } else {
        // Utiliser rawValue directement pour l'édition, même si c'est "s" ou autre valeur courte
        const editValue = rawValue || '';
        return (
          <input
            type={column.type}
            defaultValue={editValue}
            onBlur={(e) => {
              handleCellEdit(member.id, column.key, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCellEdit(member.id, column.key, e.target.value);
                setEditingCell(null);
              }
              if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            className="w-full px-2 py-1 border rounded"
            autoFocus
          />
        );
      }
    }

    const displayValue = getDisplayValue();
    
    // Gestion spéciale pour la colonne mot de passe
    if (column.key === 'password') {
      const isHovered = hoveredPassword === cellKey;
      
      return (
        <div className="flex items-center gap-1 p-1">
          <div 
            onClick={() => column.editable && setEditingCell(cellKey)}
            onMouseEnter={() => setHoveredPassword(cellKey)}
            onMouseLeave={() => setHoveredPassword(null)}
            className={`
              flex-1 cursor-pointer p-1 rounded hover:bg-gray-50 relative
              ${column.editable ? 'hover:bg-blue-50' : ''}
              ${saveStatus === 'saving' ? 'bg-yellow-50' : ''}
              ${saveStatus === 'saved' ? 'bg-green-50' : ''}
              ${saveStatus === 'error' ? 'bg-red-50' : ''}
            `}
            title="Survolez pour voir le mot de passe • Cliquez pour éditer"
          >
            <span className={displayValue === 'NC' ? 'text-gray-400 italic' : 'font-mono text-xs'}>
              {displayValue === 'NC' ? 'NC' : (isHovered ? rawValue : '••••••••••••')}
            </span>
            {saveStatus === 'saving' && <div className="absolute top-0 right-0 w-2 h-2 bg-yellow-400 rounded-full"></div>}
            {saveStatus === 'saved' && <div className="absolute top-0 right-0 w-2 h-2 bg-green-400 rounded-full"></div>}
            {saveStatus === 'error' && <div className="absolute top-0 right-0 w-2 h-2 bg-red-400 rounded-full"></div>}
          </div>
          
          {/* Bouton de copie seulement */}
          {rawValue && rawValue !== '' && (
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(rawValue);
                  alert('Mot de passe copié dans le presse-papiers !');
                } catch (error) {
                  alert(`Mot de passe: ${rawValue}`);
                }
              }}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Copier le mot de passe"
            >
              <Copy className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }
    
    return (
      <div 
        onClick={() => column.editable && setEditingCell(cellKey)}
        className={`
          cursor-pointer p-2 rounded hover:bg-gray-50 relative
          ${column.editable ? 'hover:bg-blue-50' : ''}
          ${saveStatus === 'saving' ? 'bg-yellow-50' : ''}
          ${saveStatus === 'saved' ? 'bg-green-50' : ''}
          ${saveStatus === 'error' ? 'bg-red-50' : ''}
        `}
      >
        {column.type === 'boolean' ? (
          <input
            type="checkbox"
            checked={!!rawValue}
            onChange={(e) => {
              e.preventDefault();
              if (column.editable) {
                handleCellEdit(member.id, column.key, e.target.checked);
              }
            }}
            className="mx-auto"
            disabled={!column.editable}
          />
        ) : (
          <span className={displayValue === 'NC' ? 'text-gray-400 italic' : ''}>
            {displayValue}
          </span>
        )}
        {saveStatus === 'saving' && <div className="absolute top-0 right-0 w-2 h-2 bg-yellow-400 rounded-full"></div>}
        {saveStatus === 'saved' && <div className="absolute top-0 right-0 w-2 h-2 bg-green-400 rounded-full"></div>}
        {saveStatus === 'error' && <div className="absolute top-0 right-0 w-2 h-2 bg-red-400 rounded-full"></div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* HEADER - RESPONSIVE */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[250px]">
          <h1 className="text-3xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Base de données
          </h1>
          <p className="text-gray-600 mt-2">
            Édition directe et avancée de la base de données
          </p>
        </div>
      </div>

      {/* ONGLETS */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('members')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'members'
              ? 'border-b-2 text-amber-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'members' ? { borderColor: 'rgb(196, 150, 96)' } : {}}
        >
          Membres ({members.length})
        </button>
        <button
          onClick={() => setActiveTab('cotisations')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'cotisations'
              ? 'border-b-2 text-amber-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'cotisations' ? { borderColor: 'rgb(196, 150, 96)' } : {}}
        >
          Cotisations ({cotisations.length})
        </button>
        <button
          onClick={() => setActiveTab('multiCotisations')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'multiCotisations'
              ? 'border-b-2 text-amber-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'multiCotisations' ? { borderColor: 'rgb(196, 150, 96)' } : {}}
        >
          Paiements groupés ({multiCotisations.length})
        </button>
        <button
          onClick={() => setActiveTab('donations')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'donations'
              ? 'border-b-2 text-amber-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'donations' ? { borderColor: 'rgb(196, 150, 96)' } : {}}
        >
          Donations ({donations.length})
        </button>
      </div>

      {/* CONTENU DONATIONS */}
      {activeTab === 'donations' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Donateur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {donations.map((donation) => (
                <tr key={donation.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{donation.user_name}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{donation.user_email}</td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">{donation.amount}€</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      donation.status === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : donation.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {donation.status === 'paid' ? 'Payé' : donation.status === 'pending' ? 'En attente' : donation.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {donation.created_at ? new Date(donation.created_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      {donation.status === 'pending' && (
                        <button
                          onClick={() => markDonationPaid(donation.id, donation.user_name)}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          ✓ Payé
                        </button>
                      )}
                      {donation.status === 'paid' && (
                        <button
                          onClick={() => markDonationPending(donation.id, donation.user_name)}
                          className="text-orange-600 hover:text-orange-800 font-medium"
                        >
                          ⏸ Attente
                        </button>
                      )}
                      <button
                        onClick={() => deleteDonation(donation.id, donation.user_name)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {donations.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Aucune donation trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* CONTENU PAIEMENTS GROUPÉS */}
      {activeTab === 'multiCotisations' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payeur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Année</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bénéficiaires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {multiCotisations.map((mp) => (
                <tr key={mp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{mp.payer_name}</div>
                    <div className="text-xs text-gray-500">{mp.payer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">{mp.year}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="space-y-1">
                      {mp.beneficiaries?.map((b, idx) => (
                        <div key={idx} className={`text-xs ${b.member_found ? 'text-green-700' : 'text-orange-600'}`}>
                          {b.member_found ? '✓' : '⚠️'} {b.first_name} {b.last_name}
                          {b.email && <span className="text-gray-400 ml-1">({b.email})</span>}
                          {!b.member_found && <span className="ml-1 text-orange-500">(non trouvé)</span>}
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {mp.beneficiaries_updated || 0}/{mp.beneficiaries_count} mis à jour
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-bold">{mp.total_amount}€</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      mp.status === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : mp.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {mp.status === 'paid' ? 'Payé' : mp.status === 'pending' ? 'En attente' : mp.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {mp.created_at ? new Date(mp.created_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      {mp.status === 'pending' && (
                        <button
                          onClick={() => markMultiCotisationPaid(mp.id, mp.payer_name)}
                          className="text-green-600 hover:text-green-800 font-medium"
                          data-testid={`mark-paid-multi-${mp.id}`}
                        >
                          ✓ Payé
                        </button>
                      )}
                      {mp.status === 'paid' && (
                        <button
                          onClick={() => markMultiCotisationPending(mp.id, mp.payer_name)}
                          className="text-orange-600 hover:text-orange-800 font-medium"
                          data-testid={`mark-pending-multi-${mp.id}`}
                        >
                          ⏸ Attente
                        </button>
                      )}
                      <button
                        onClick={() => deleteMultiCotisation(mp.id, mp.payer_name)}
                        className="text-red-600 hover:text-red-800 font-medium"
                        data-testid={`delete-multi-${mp.id}`}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {multiCotisations.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Aucun paiement groupé trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* CONTENU COTISATIONS */}
      {activeTab === 'cotisations' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Membre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Année</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cotisations.map((cot) => (
                <tr key={cot.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">
                      {cot.member_first_name || ''} {cot.member_last_name || ''}
                    </div>
                    <div className="text-xs text-gray-500">{cot.member_email || cot.member_id?.slice(0, 8) + '...'}</div>
                    {cot.paid_by_name && cot.paid_by_name !== `${cot.member_first_name} ${cot.member_last_name}` && (
                      <div className="text-xs text-blue-600 mt-1">
                        💳 Payé par: {cot.paid_by_name}
                      </div>
                    )}
                    {cot.multi_payment_id && (
                      <div className="text-xs text-purple-600">
                        👥 Paiement groupé
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">{cot.year}</td>
                  <td className="px-4 py-3 text-sm">{cot.amount}€</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      cot.status === 'paid' || cot.status === 'payee' 
                        ? 'bg-green-100 text-green-800' 
                        : cot.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {cot.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{cot.payment_method || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {cot.created_at ? new Date(cot.created_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      {(cot.status === 'pending' || cot.status === 'en_attente') && (
                        <button
                          onClick={async () => {
                            if (window.confirm(`Marquer la cotisation de ${cot.member_first_name} ${cot.member_last_name} comme payée ?`)) {
                              try {
                                const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/${cot.id}/mark-paid`, {
                                  method: 'PATCH',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  alert('Cotisation marquée comme payée !');
                                  fetchCotisations();
                                } else {
                                  alert('Erreur lors de la mise à jour');
                                }
                              } catch (e) {
                                alert('Erreur: ' + e.message);
                              }
                            }
                          }}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          ✓ Payé
                        </button>
                      )}
                      {(cot.status === 'paid' || cot.status === 'payee') && (
                        <button
                          onClick={async () => {
                            if (window.confirm(`Remettre la cotisation de ${cot.member_first_name} ${cot.member_last_name} en attente ?`)) {
                              try {
                                const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/${cot.id}/mark-pending`, {
                                  method: 'PATCH',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  alert('Cotisation remise en attente');
                                  fetchCotisations();
                                } else {
                                  alert('Erreur lors de la mise à jour');
                                }
                              } catch (e) {
                                alert('Erreur: ' + e.message);
                              }
                            }
                          }}
                          className="text-orange-600 hover:text-orange-800 font-medium"
                        >
                          ⏸ Attente
                        </button>
                      )}
                      <button
                        onClick={() => deleteCotisation(cot.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {cotisations.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Aucune cotisation trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* CONTENU MEMBRES */}
      {activeTab === 'members' && (
      <>
      {/* Ligne 1 : Boutons Excel */}
      <div className="flex flex-wrap gap-2 w-full md:w-auto md:flex-nowrap">
        <Button 
            onClick={async () => {
              try {
                const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/template`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'template_membres.xlsx';
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                }
              } catch (error) {
                console.error('Download template error:', error);
              }
            }}
            variant="outline"
            className="border-2"
            style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Template Excel
          </Button>
          <input
            type="file"
            id="excel-import-input"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              
              setImporting(true);
              setImportProgress('Préparation...');
              
              const formData = new FormData();
              formData.append('file', file);
              
              try {
                setImportProgress('Upload du fichier...');
                const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-members`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` },
                  body: formData
                });
                
                setImportProgress('Traitement des données...');
                
                if (response.ok) {
                  const result = await response.json();
                  setImportProgress('Terminé !');
                  const message = `✅ Import terminé avec succès !\n\n` +
                    `📊 Résultats :\n` +
                    `• Nouveaux membres créés : ${result.imported_count}\n` +
                    `• Membres existants mis à jour : ${result.updated_count || 0}\n` +
                    `• Comptes incomplets : ${result.incomplete_count || 0}\n` +
                    `• Total traité : ${result.total_processed}\n` +
                    (result.errors && result.errors.length > 0 ? `\n⚠️ Erreurs :\n${result.errors.join('\n')}` : '');
                  alert(message);
                  window.location.reload();
                } else {
                  const error = await response.json();
                  alert(`Erreur lors de l'import:\n${error.detail || 'Erreur inconnue'}`);
                  setImporting(false);
                  setImportProgress('');
                }
              } catch (error) {
                console.error('Import error:', error);
                alert('Erreur lors de l\'import: ' + error.message);
                setImporting(false);
                setImportProgress('');
              }
              
              e.target.value = '';
            }}
          />
          <Button 
            onClick={() => {
              document.getElementById('excel-import-input')?.click();
            }}
            variant="outline"
            className="border-2"
            style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
            disabled={importing}
          >
            {importing ? (
              <>
                <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                {importProgress || 'Import en cours...'}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import Excel
              </>
            )}
          </Button>
          <Button 
            onClick={() => {
              // Fonction d'export Excel
              const exportData = async () => {
                try {
                  console.log('🚀 Export Excel - Début');
                  const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                  
                  if (!token) {
                    console.error('❌ Aucun token trouvé');
                    alert('Erreur: Vous devez être connecté pour exporter');
                    return;
                  }
                  
                  console.log('📡 Appel API export...');
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-members`, {
                    method: 'GET',
                    headers: { 
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  
                  console.log('📥 Réponse reçue:', response.status, response.statusText);
                  
                  if (response.ok) {
                    const blob = await response.blob();
                    console.log('📦 Blob créé, taille:', blob.size, 'bytes');
                    
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `membres_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    
                    console.log('✅ Export terminé avec succès');
                  } else {
                    const errorText = await response.text();
                    console.error('❌ Erreur serveur:', response.status, errorText);
                    alert(`Erreur d'export: ${response.status} - ${errorText.substring(0, 100)}`);
                  }
                } catch (error) {
                  console.error('❌ Erreur export:', error);
                  alert(`Erreur: ${error.message}`);
                }
              };
              exportData();
            }}
            variant="outline"
            className="border-2"
            style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
        
        {/* Ligne 2 : Boutons JSON */}
        <div className="flex flex-wrap gap-2 w-full md:w-auto md:flex-nowrap">
          <Button
            onClick={handleExportJSON}
            disabled={loading}
            variant="outline"
            className="border-2"
            style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
          >
            <Download className="w-4 h-4 mr-2" />
            Export JSON
          </Button>
          
          <Button
            onClick={() => document.getElementById('json-import-input')?.click()}
            disabled={importing}
            variant="outline"
            className="border-2"
            style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Import JSON
          </Button>
          
          <input
            id="json-import-input"
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImportJSON}
          />
        </div>


      {/* CONTRÔLES - LARGEUR STRICTEMENT LIMITÉE */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Recherche - ligne complète */}
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Rechercher dans tous les champs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
            </div>

            {/* Contrôles - Responsive avec wrap */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Taille de page + Colonnes - restent ensemble */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Taille de page */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="whitespace-nowrap">Afficher:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </select>
                  <span className="whitespace-nowrap">lignes</span>
                </div>

                {/* Colonnes */}
                <details className="relative">
                  <summary className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50">
                    <Filter className="w-4 h-4" />
                    <span className="whitespace-nowrap">Colonnes</span>
                  </summary>
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2 min-w-48">
                    {columns.map(column => (
                      <label key={column.key} className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.has(column.key)}
                          onChange={() => toggleColumnVisibility(column.key)}
                        />
                        <span className="text-sm">{column.label}</span>
                      </label>
                    ))}
                  </div>
                </details>
              </div>

              {/* Boutons de contrôle - peuvent passer à la ligne sur mobile */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Auto-ajuster */}
                <button
                  onClick={autoSizeColumns}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 whitespace-nowrap"
                  title="Ajuster automatiquement la largeur des colonnes au contenu"
                >
                  Auto-ajuster
                </button>

                {/* Réinitialiser colonnes */}
                <button
                  onClick={() => setColumnWidths({})}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 whitespace-nowrap"
                  title="Remettre les colonnes à leur taille par défaut"
                >
                  Réinitialiser colonnes
                </button>

                {/* Actualiser */}
                <button
                  onClick={fetchMembers}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 whitespace-nowrap"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DATATABLE - CONTAINER ISOLÉ AVEC SCROLL HORIZONTAL */}
      <Card>
        <CardContent className="p-0">
          {/* SCROLL HORIZONTAL SEULEMENT ICI */}
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="text-sm" style={{ minWidth: '1400px' }}>
                <thead>
                  <tr className="border-b bg-gray-50">
                    {columns.filter(col => !hiddenColumns.has(col.key)).map(column => (
                      <th 
                        key={column.key} 
                        className="text-left p-3 select-none relative group border-r border-gray-200 whitespace-nowrap"
                        style={{ 
                          width: columnWidths[column.key] || '160px',
                          minWidth: '100px',
                          position: 'relative'
                        }}
                      >
                        <div 
                          className="flex items-center justify-between cursor-pointer hover:bg-gray-100 -m-3 p-3 rounded"
                          onClick={() => handleSort(column.key)}
                        >
                          <span className="font-medium">{column.label}</span>
                          <div className="flex flex-col">
                            <ChevronUp 
                              className={`w-3 h-3 ${
                                sortConfig.key === column.key && sortConfig.direction === 'asc' 
                                  ? 'text-blue-600' 
                                  : 'text-gray-400'
                              }`} 
                            />
                            <ChevronDown 
                              className={`w-3 h-3 -mt-1 ${
                                sortConfig.key === column.key && sortConfig.direction === 'desc' 
                                  ? 'text-blue-600' 
                                  : 'text-gray-400'
                              }`} 
                            />
                          </div>
                        </div>
                        
                        {/* Poignée de redimensionnement */}
                        <div
                          className="absolute top-0 right-0 w-2 h-full cursor-col-resize bg-transparent hover:bg-blue-400 border-r-2 border-transparent hover:border-blue-400 transition-all"
                          onMouseDown={(e) => handleMouseDown(e, column.key)}
                          title="Glisser pour redimensionner la colonne"
                        >
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-1 h-6 bg-gray-300 hover:bg-blue-600 transition-colors"></div>
                          </div>
                        </div>
                        
                        {/* Indicateur de redimensionnement actif */}
                        {resizing === column.key && (
                          <div className="absolute top-0 right-0 w-2 h-full bg-blue-500 shadow-lg z-10"></div>
                        )}
                      </th>
                    ))}
                    {/* Colonne Actions fixe */}
                    <th className="text-center p-3 bg-gray-50 whitespace-nowrap" style={{ width: '100px', minWidth: '100px' }}>
                      <span className="font-medium">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMembers.map((member, index) => (
                    <tr key={member.id} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                      {columns.filter(col => !hiddenColumns.has(col.key)).map(column => (
                        <td 
                          key={`${member.id}-${column.key}`} 
                          className="p-0 border-r border-gray-100 whitespace-nowrap overflow-hidden"
                          style={{ 
                            width: columnWidths[column.key] || '150px',
                            maxWidth: columnWidths[column.key] || '150px',
                            minWidth: '80px'
                          }}
                        >
                          {renderCell(member, column)}
                        </td>
                      ))}
                      {/* Cellule Actions */}
                      <td className="p-2 text-center border-r border-gray-100 whitespace-nowrap" style={{ width: '100px', minWidth: '100px' }}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => confirmDelete(member)}
                          disabled={member.role === 'admin' || deleting === member.id}
                          className={`
                            ${member.role === 'admin' 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            }
                            ${deleting === member.id ? 'opacity-50' : ''}
                          `}
                          title={member.role === 'admin' ? 'Impossible de supprimer un administrateur' : 'Supprimer ce membre'}
                        >
                          {deleting === member.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          
            {/* PAGINATION - SÉPARÉE DU SCROLL, LARGEUR NORMALE */}
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-gray-500">
                Affichage de {((currentPage - 1) * pageSize) + 1} à {Math.min(currentPage * pageSize, filteredAndSortedMembers.length)} sur {filteredAndSortedMembers.length} membres
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = Math.max(1, currentPage - 2) + i;
                  if (page > totalPages) return null;
                  return (
                    <Button
                      key={page}
                      variant={page === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={page === currentPage ? "text-white" : ""}
                      style={page === currentPage ? { backgroundColor: 'rgb(196, 150, 96)' } : {}}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </Button>
              </div>
            </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Modal de confirmation de suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">
                Confirmer la suppression
              </h2>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Êtes-vous sûr de vouloir supprimer ce membre ?
              </p>
              <div className="bg-gray-50 p-3 rounded border">
                <p className="font-semibold">
                  {deleteConfirm.first_name} {deleteConfirm.last_name}
                </p>
                <p className="text-sm text-gray-600">
                  {deleteConfirm.email}
                </p>
                <p className="text-sm text-gray-600">
                  Rôle: {deleteConfirm.role}
                </p>
              </div>
              <p className="text-red-600 text-sm mt-2">
                ⚠️ Cette action est irréversible !
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting === deleteConfirm.id}
              >
                Annuler
              </Button>
              <Button
                onClick={() => handleDeleteMember(deleteConfirm)}
                disabled={deleting === deleteConfirm.id}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting === deleteConfirm.id ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bouton d'aide flottant */}
      <HelpButton pageId="database-management" />
    </div>
  );
};

export default DatabaseManagement;